/* Canvello v8 — the bag (Task 5). snippets/cv-bag-drawer.liquid (drawer, on
 * every page but the cart), sections/cv-cart.liquid (cart page); both hold
 * snippets/cv-bag-body.liquid, its lines snippets/cv-bag-line.liquid.
 *
 * Drawer  <cv-bag-drawer>: a native modal <dialog> (v8:L1181-1236). The page
 *         behind is inert and does not scroll; it slides in from the inline
 *         end (cvSlide mirrors in RTL). Focus goes to Close on opening; Tab
 *         wraps inside; Escape, Close and the scrim close it, and focus goes
 *         back to what opened it.
 *         Opened by the header's Bag (a[data-cv-bag]: a link to /cart without
 *         JS, a button with it: aria-haspopup, aria-expanded, Space too) and
 *         by every add: a cart:updated event from anything but the bag itself
 *         (card quick add, quick view, product form, buy bar) refreshes it and
 *         opens it, saying "Added" with the new count and subtotal; detail
 *         open: false refreshes only. An add from quick view closes quick view
 *         first, so focus lands back on the card when the bag closes.
 * Lines   (drawer and page) − / + / Remove are links to /cart/change without
 *         JS, buttons with it. − and + step the field at once and send the
 *         last value 350ms after the last press; a typed quantity is sent on
 *         change (Enter or leaving the field); Remove is sent at once. Each
 *         goes to /cart/change.js with sections= every bag on the page; their
 *         insides are swapped, focus stays on the same control of the same line
 *         (a removed line hands it to the next line, the previous one, or the
 *         empty state's link), one message says what changed with the new count
 *         and subtotal, and cart:updated (source cv-bag, quiet) moves the
 *         header's count without a second announcement.
 * Note    Saved as it is typed (/cart/update.js, 600ms after the last key);
 *         the cart form posts it with Checkout anyway.
 */
(() => {
  if (window.CVBag) return;

  const root = document.documentElement;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  const visible = (el) => !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const route = (k, d) => (window.routes && window.routes[k]) || d;
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  const slots = () => Array.from(document.querySelectorAll('[data-cv-bag-slot]'));
  const liveFor = (el) => {
    const host = el.closest('[data-cv-bag-dialog], [data-cv-cart]');
    return host ? host.querySelector('[data-cv-bag-live]') : null;
  };
  const say = (live, text) => {
    if (!live || !text) return;
    live.textContent = '';
    setTimeout(() => (live.textContent = text), 60);
  };
  const fill = (tpl, map) => Object.keys(map).reduce((s, k) => s.split(`[${k}]`).join(map[k]), tpl || '');

  /* ---- Rendering --------------------------------------------------------- */

  // − / + / Remove act as buttons once the script runs.
  function enhance(scope) {
    scope.querySelectorAll('a[data-cv-line-set]').forEach((a) => a.setAttribute('role', 'button'));
  }

  // Swap every bag on the page from a Section Rendering API map.
  function render(sections) {
    for (const slot of slots()) {
      const html = sections && sections[slot.dataset.section];
      if (!html) continue;
      const inner = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cv-bag-inner]');
      if (!inner) continue;
      slot.replaceChildren(document.adoptNode(inner));
      enhance(slot);
      const count = slot.closest('[data-cv-cart]')?.querySelector('[data-cv-cart-count]');
      if (count) count.textContent = inner.dataset.items;
    }
  }

  // A GET of ?sections= on a product URL comes back from the storefront's
  // cache with an old bag (seen 2026-10-03); a POST to /cart/update.js with
  // nothing to update renders the sections fresh, as a line change does.
  function refresh() {
    const ids = slots().map((s) => s.dataset.section);
    if (!ids.length) return Promise.resolve();
    return fetch(route('cart_update_url', '/cart/update') + '.js', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ sections: ids, sections_url: location.pathname }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => body && body.sections && render(body.sections))
      .catch(() => {});
  }

  /* ---- Line changes ------------------------------------------------------ */

  const timers = new Map();
  let busy = Promise.resolve();

  function where(el) {
    const line = el.closest('[data-cv-line]');
    const slot = el.closest('[data-cv-bag-slot]');
    return { line, slot, key: line && line.dataset.cvLine, index: line && Number(line.dataset.index) };
  }

  // A line's key changes whenever its discount does (2026-10-17: the Pair and
  // Set of four automatic discounts re-key a line on every quantity change),
  // so a line is found again by its variant (the key's first part) nearest to
  // where it was, never by the key alone.
  const variantOf = (k) => String(k || '').split(':')[0];
  function findLine(slot, key, index) {
    const lines = Array.from(slot.querySelectorAll('[data-cv-line]'));
    const exact = lines.find((l) => l.dataset.cvLine === key);
    if (exact) return exact;
    const same = lines.filter((l) => variantOf(l.dataset.cvLine) === variantOf(key));
    if (!same.length) return null;
    return same.reduce((a, b) => (Math.abs(Number(b.dataset.index) - index) < Math.abs(Number(a.dataset.index) - index) ? b : a));
  }

  // After a swap: the same control of the same line, else a neighbour.
  function refocus(slot, key, kind, index) {
    const lines = Array.from(slot.querySelectorAll('[data-cv-line]'));
    const same = findLine(slot, key, index);
    let target = null;
    if (same) {
      target = same.querySelector(`[data-kind="${kind}"]:not([data-cv-line-max])`) || same.querySelector('[data-kind="qty"]');
    } else if (lines.length) {
      const next = lines[Math.min(index - 1, lines.length - 1)];
      target = next.querySelector('[data-kind="name"]');
    } else {
      target = slot.querySelector('[data-cv-bag-shop]');
    }
    if (target) target.focus({ preventScroll: false });
  }

  function change(el, qty) {
    const { line, slot, key, index } = where(el);
    if (!line || !slot) return;
    const kind = el.dataset.kind || 'qty';
    const hadFocus = slot.contains(document.activeElement);
    const title = line.dataset.title;
    const live = liveFor(slot);
    const err = line.querySelector('[data-cv-line-err]');
    const ids = slots().map((s) => s.dataset.section);
    let at = index;
    let was = key;
    busy = busy.then(() => {
      slot.setAttribute('aria-busy', 'true');
      // The line as it is now (an earlier change may have re-keyed it).
      const cur = findLine(slot, key, index);
      if (cur) {
        at = Number(cur.dataset.index) || index;
        was = cur.dataset.cvLine;
      }
      return fetch(route('cart_change_url', '/cart/change') + '.js', {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ line: at, quantity: qty, sections: ids, sections_url: location.pathname }),
      })
        .then((r) => r.json().then((body) => ({ ok: r.ok, body })))
        .then(({ ok, body }) => {
          if (!ok) throw new Error(body.description || body.message || (live && live.dataset.error));
          const { sections, ...cart } = body;
          render(sections);
          const inner = slot.querySelector('[data-cv-bag-inner]');
          const here = cart.items[at - 1];
          const now = cart.items.find((i) => i.key === was) || (qty > 0 && here && variantOf(here.key) === variantOf(was) ? here : null);
          let msg;
          if (!now) msg = fill(live && live.dataset.removed, { title });
          else {
            msg = fill(live && live.dataset.changed, { title, quantity: String(now.quantity) });
            if (now.quantity < qty && live) msg += ' ' + fill(live.dataset.max, { count: String(now.quantity) });
          }
          say(live, `${msg} ${inner ? (cart.item_count ? inner.dataset.summary : inner.dataset.emptySaid) : ''}`.trim());
          const lost = !document.activeElement || document.activeElement === document.body;
          if (hadFocus || lost) refocus(slot, was, kind, at);
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart, item_count: cart.item_count, source: 'cv-bag', quiet: true } }));
        })
        .catch((e) => {
          const msg = (e && e.message) || (live && live.dataset.error) || '';
          const input = line.querySelector('[data-cv-line-qty]');
          if (input && document.contains(input)) input.value = line.dataset.qty;
          if (err && document.contains(err)) {
            err.textContent = msg;
            err.hidden = !msg;
          } else say(live, msg);
        })
        .finally(() => slot.removeAttribute('aria-busy'));
    });
  }

  function step(btn) {
    const { line } = where(btn);
    const input = line && line.querySelector('[data-cv-line-qty]');
    if (!input) return;
    const by = Number(input.step) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    let v = (Number(input.value) || 0) + (btn.dataset.kind === 'inc' ? by : -by);
    v = Math.max(0, Math.min(v, max));
    if (String(v) === input.value) return;
    input.value = String(v);
    clearTimeout(timers.get(line.dataset.cvLine));
    if (v === 0) return change(btn, 0);
    timers.set(line.dataset.cvLine, setTimeout(() => change(btn, v), 350));
  }

  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('[data-cv-bag-slot] a[data-cv-line-set]');
    if (!a || !window.fetch) return;
    e.preventDefault();
    if (a.dataset.kind === 'rm') {
      clearTimeout(timers.get(where(a).key));
      change(a, 0);
    } else step(a);
  });
  document.addEventListener('keydown', (e) => {
    const t = e.target;
    if (e.key === ' ' && t.matches && t.matches('[data-cv-bag-slot] a[data-cv-line-set]')) {
      e.preventDefault();
      t.click();
    }
    // Enter in a quantity sends it here, not the form to /cart.
    if (e.key === 'Enter' && t.matches && t.matches('[data-cv-bag-slot] [data-cv-line-qty]')) {
      e.preventDefault();
      t.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest && e.target.closest('[data-cv-bag-slot] [data-cv-line-qty]');
    if (!input) return;
    const { line } = where(input);
    const v = Math.max(0, Math.floor(Number(input.value)));
    if (!Number.isFinite(v) || String(v) === line.dataset.qty) {
      input.value = line.dataset.qty;
      return;
    }
    // Enter sends it here and Chrome fires its own change too: once.
    if (input.dataset.sent === String(v)) return;
    input.dataset.sent = String(v);
    clearTimeout(timers.get(line.dataset.cvLine));
    change(input, v);
  });

  /* ---- Note -------------------------------------------------------------- */

  let noteTimer = 0;
  document.addEventListener('input', (e) => {
    const field = e.target.closest && e.target.closest('[data-cv-bag-note]');
    if (!field) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => {
      fetch(route('cart_update_url', '/cart/update') + '.js', {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ note: field.value }),
      }).catch(() => {});
    }, 600);
  });

  /* ---- Drawer ------------------------------------------------------------ */

  class CVBagDrawer extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      this.dialog = this.querySelector('[data-cv-bag-dialog]');
      this.returnTo = null;
      if (!this.dialog) return;
      const on = (t, ev, fn) => t.addEventListener(ev, fn, { signal: this.ac.signal });
      const d = this.dialog;

      this.bags().forEach((a) => this.wire(a));
      on(document, 'click', (e) => {
        const a = e.target.closest && e.target.closest('a[data-cv-bag]');
        if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        this.open({ from: a });
      });
      on(document, 'keydown', (e) => {
        if (e.key === ' ' && e.target.matches && e.target.matches('a[data-cv-bag]')) {
          e.preventDefault();
          this.open({ from: e.target });
        }
      });

      on(d, 'cancel', (e) => {
        e.preventDefault();
        this.close();
      });
      on(d, 'close', () => {
        d.removeAttribute('data-closing');
        root.removeAttribute('data-cv-locked');
        this.bags().forEach((a) => a.setAttribute('aria-expanded', 'false'));
        const back = this.returnTo;
        this.returnTo = null;
        if (back && document.contains(back) && visible(back)) back.focus();
      });
      on(d, 'click', (e) => {
        if (e.target.closest('[data-cv-bag-close]')) return this.close();
        if (e.target === d) {
          const r = d.getBoundingClientRect();
          if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) this.close();
        }
      });
      on(d, 'keydown', (e) => {
        if (e.key !== 'Tab') return;
        const items = Array.from(d.querySelectorAll(FOCUSABLE)).filter(visible);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && (document.activeElement === first || !d.contains(document.activeElement))) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (document.activeElement === last || !d.contains(document.activeElement))) {
          e.preventDefault();
          first.focus();
        }
      });

      // Every add, from anywhere but the bag.
      on(document, 'cart:updated', (e) => {
        const detail = e.detail || {};
        if (detail.source === 'cv-bag') return;
        refresh().then(() => {
          if (detail.open === false) return;
          this.open({ added: true });
        });
      });
      // A page restored from the back/forward cache shows an old bag.
      on(window, 'pageshow', (e) => e.persisted && refresh());
      enhance(this);
    }

    disconnectedCallback() {
      this.ac.abort();
      if (this.dialog && this.dialog.open) this.dialog.close();
      root.removeAttribute('data-cv-locked');
    }

    bags() {
      return Array.from(document.querySelectorAll('a[data-cv-bag]'));
    }

    // The header's Bag: a link to /cart that opens this dialog instead.
    wire(a) {
      a.setAttribute('role', 'button');
      a.setAttribute('aria-haspopup', 'dialog');
      a.setAttribute('aria-controls', this.dialog.id);
      a.setAttribute('aria-expanded', 'false');
    }

    open({ from = null, added = false } = {}) {
      const d = this.dialog;
      // Quick view first: it hands focus back to its card as it closes.
      const qv = document.querySelector('dialog.cv-qv[open]');
      if (qv && window.CVQuickView) {
        qv.addEventListener('close', () => requestAnimationFrame(() => this.open({ from, added })), { once: true });
        window.CVQuickView.close();
        return;
      }
      if (d.open) {
        if (added) this.sayAdded();
        return;
      }
      const active = document.activeElement;
      this.returnTo = from || (active && active !== document.body ? active : null);
      d.removeAttribute('data-closing');
      d.showModal();
      root.setAttribute('data-cv-locked', '');
      this.bags().forEach((a) => a.setAttribute('aria-expanded', 'true'));
      const close = d.querySelector('[data-cv-bag-close]');
      if (close) close.focus();
      const scroll = d.querySelector('.cv-bagd__scroll');
      if (scroll) scroll.scrollTop = 0;
      if (added) this.sayAdded();
    }

    sayAdded() {
      const live = this.dialog.querySelector('[data-cv-bag-live]');
      const inner = this.dialog.querySelector('[data-cv-bag-inner]');
      if (live && inner) say(live, `${live.dataset.added} ${inner.dataset.summary}`);
    }

    close(instant = false) {
      const d = this.dialog;
      if (!d || !d.open || d.hasAttribute('data-closing')) return;
      let timer = 0;
      const end = (e) => e.target === d && done();
      const done = () => {
        clearTimeout(timer);
        d.removeEventListener('animationend', end);
        if (d.open) d.close(); // the close listener puts the page back
      };
      if (instant || reduced()) return done();
      d.setAttribute('data-closing', '');
      d.addEventListener('animationend', end);
      timer = setTimeout(done, 700);
    }
  }

  customElements.define('cv-bag-drawer', CVBagDrawer);

  // The cart page (no drawer there): its controls need the button roles too.
  const page = document.querySelector('[data-cv-cart]');
  if (page) enhance(page);
  else document.addEventListener('DOMContentLoaded', () => {
    const p = document.querySelector('[data-cv-cart]');
    if (p) enhance(p);
  });

  window.CVBag = {
    open: (opts) => document.querySelector('cv-bag-drawer')?.open(opts),
    close: (instant) => document.querySelector('cv-bag-drawer')?.close(instant),
    refresh,
  };
})();
